import React, { useState, useMemo } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import axios from "axios";
import SiteFooter from "../components/SiteFooter";

const Global = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f0ebe3; font-family: 'Pretendard', "Noto Sans KR", sans-serif; }
`;

/* ─── 공통 레이아웃 ─────────────────────────────────────────────────── */
const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px 60px;
`;
const Header = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;
const Logo = styled.div`font-size: 2.2rem; margin-bottom: 6px;`;
const Title = styled.h1`font-size: 1.4rem; color: #3d2b1f; font-weight: 800;`;
const Sub = styled.p`font-size: 0.82rem; color: #7a6654; margin-top: 4px;`;

/* ─── STEP 0: 서비스 선택(훅) 페이지 ──────────────────────────────── */
const HookWrap = styled.div`
  width: 100%;
  max-width: 560px;
`;
const HookTitle = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;
const HookTitleMain = styled.h2`
  font-size: 1.25rem;
  font-weight: 900;
  color: #3d2b1f;
  margin-bottom: 6px;
`;
const HookTitleSub = styled.p`
  font-size: 0.8rem;
  color: #7a6654;
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ServiceCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 400px) { grid-template-columns: 1fr; }
`;

const ServiceCard = styled.button<{ $color: string; $idx: number }>`
  background: linear-gradient(145deg, ${p => p.$color}18, ${p => p.$color}08);
  border: 1.5px solid ${p => p.$color}40;
  border-radius: 18px;
  padding: 20px 16px 18px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: ${p => p.$idx * 0.06}s;
  position: relative;
  overflow: hidden;
  font-family: inherit;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: ${p => p.$color};
    opacity: 0.6;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: ${p => p.$color}80;
    background: linear-gradient(145deg, ${p => p.$color}28, ${p => p.$color}14);
    box-shadow: 0 8px 24px ${p => p.$color}22;
  }
  &:active { transform: translateY(-1px); }
`;
const SvcIcon = styled.div`font-size: 1.8rem; margin-bottom: 8px; line-height: 1;`;
const SvcHook = styled.div<{ $color: string }>`
  font-size: 13.5px;
  font-weight: 800;
  color: ${p => p.$color};
  line-height: 1.45;
  margin-bottom: 6px;
`;
const SvcSub = styled.div`
  font-size: 10.5px;
  color: #7a6654;
  line-height: 1.4;
  margin-bottom: 14px;
`;
const SvcBtn = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.$color};
  background: ${p => p.$color}18;
  border-radius: 20px;
  padding: 5px 12px;
`;

/* ─── STEP 1: 입력 폼 페이지 ────────────────────────────────────────── */
const FormWrap = styled.div`
  width: 100%;
  max-width: 520px;
`;
const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #9c7b5a;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 0 16px;
  font-family: inherit;
  &:hover { color: #7a6654; }
`;

const ServiceHeaderBand = styled.div<{ $color: string }>`
  background: linear-gradient(135deg, ${p => p.$color}22, ${p => p.$color}0a);
  border-bottom: 2px solid ${p => p.$color}30;
  padding: 14px 18px 12px;
`;
const ServiceHeaderIcon = styled.span`font-size: 1.3rem; margin-right: 8px;`;
const ServiceHeaderHook = styled.div<{ $color: string }>`
  font-size: 14px;
  font-weight: 800;
  color: ${p => p.$color};
  line-height: 1.4;
`;
const ServiceHeaderSub = styled.div`font-size: 11px; color: #868E96; margin-top: 3px;`;

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 0 0 28px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.18);
  overflow: hidden;
`;

/* ─── 하위 옵션 칩 ──────────────────────────────────────────────────── */
const SubOptWrap = styled.div<{ $visible: boolean }>`
  overflow: hidden;
  max-height: ${p => p.$visible ? "150px" : "0"};
  transition: max-height 0.25s ease;
  background: #F8F9FA;
  border-bottom: 1px solid #E9ECEF;
`;
const SubOptInner = styled.div`padding: 12px 16px;`;
const SubOptLabel = styled.div`font-size: 11px; font-weight: 700; color: #868E96; margin-bottom: 8px; letter-spacing: 0.06em;`;
const ChipRow = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;
const Chip = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 16px;
  border: 1.5px solid ${p => p.$active ? "#c09a6b" : "#DEE2E6"};
  background: ${p => p.$active ? "#FFF3E6" : "#fff"};
  color: ${p => p.$active ? "#8b5e3c" : "#495057"};
  font-size: 12.5px;
  font-weight: ${p => p.$active ? 700 : 400};
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  &:hover { border-color: #c09a6b; }
`;

/* ─── 폼 ──────────────────────────────────────────────────────────── */
const FormBody = styled.form`padding: 20px 20px 0;`;
const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9c7b5a;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 18px 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  &:first-child { margin-top: 0; }
`;
const FieldRow = styled.div`display: flex; gap: 8px; margin-bottom: 10px;`;
const Field = styled.div`flex: 1; display: flex; flex-direction: column; gap: 4px;`;
const Label = styled.label`font-size: 12px; color: #5a4a3a; font-weight: 600;`;
const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e0d5c8;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #3d2b1f;
  background: #fdfaf7;
  font-family: inherit;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #c09a6b; }
`;
const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e0d5c8;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #3d2b1f;
  background: #fdfaf7;
  font-family: inherit;
  &:focus { outline: none; border-color: #c09a6b; }
`;

/* ─── 파트너 폼 ─────────────────────────────────────────────────────── */
const CompatNoticeBand = styled.div`
  margin: 0;
  padding: 12px 20px;
  background: #FFF5F5;
  border-bottom: 1px solid #FFE3E3;
`;
const CompatNoticeTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #E03131;
  margin-bottom: 4px;
`;
const CompatNoticeText = styled.div`
  font-size: 11.5px;
  color: #555;
  line-height: 1.6;
`;
const PartnerWrap = styled.div<{ $visible: boolean }>`
  overflow: hidden;
  max-height: ${p => p.$visible ? "700px" : "0"};
  transition: max-height 0.3s ease;
`;
const PartnerDivider = styled.div`
  margin: 0 -20px 0;
  padding: 14px 20px 0;
  background: linear-gradient(180deg, #F8F9FA 0%, #fff 100%);
  border-top: 2px dashed #E0D5C8;
`;
const PartnerTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #1A1A2E;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;
const PartnerSub = styled.div`
  font-size: 11px;
  color: #868E96;
  margin-bottom: 14px;
`;

/* ─── 동의 / 제출 ───────────────────────────────────────────────────── */
const ConsentBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.8rem;
  color: #5a4a3a;
  line-height: 1.5;
  margin: 20px 0 0;
  cursor: pointer;
  input { margin-top: 2px; accent-color: #c09a6b; width: 16px; height: 16px; flex-shrink: 0; }
`;
const SubmitBtn = styled.button<{ $disabled?: boolean }>`
  width: calc(100% - 40px);
  margin: 18px 20px 0;
  padding: 15px;
  background: ${p => p.$disabled ? "#ccc" : "linear-gradient(135deg,#c09a6b,#8b5e3c)"};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
  font-family: inherit;
  transition: opacity 0.2s;
  &:hover { opacity: ${p => p.$disabled ? 1 : 0.9}; }
`;
const ErrorMsg = styled.p`
  color: #c0392b;
  font-size: 0.82rem;
  margin: 10px 20px 0;
  text-align: center;
`;


/* ─── 완료 카드 ──────────────────────────────────────────────────────── */
const SuccessCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: #fff;
  border: 1.5px solid #e0d5c8;
  border-radius: 20px;
  padding: 48px 24px;
  box-shadow: 0 4px 24px rgba(100,70,40,0.1);
  text-align: center;
`;
const SuccessIcon = styled.div`font-size: 3rem; margin-bottom: 16px;`;
const SuccessTitle = styled.h2`font-size: 1.3rem; color: #3d2b1f; margin-bottom: 10px;`;
const SuccessDesc = styled.p`font-size: 0.88rem; color: #5a4a3a; line-height: 1.7;`;
const ServiceBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #FFF3E6;
  border: 1px solid #e0d5c8;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  color: #8b5e3c;
  margin-bottom: 16px;
`;
const ResetBtn = styled.button`
  margin-top: 24px;
  padding: 13px 32px;
  background: linear-gradient(135deg,#c09a6b,#8b5e3c);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:hover { opacity: 0.9; }
`;

/* ─── 상수 ───────────────────────────────────────────────────────────── */
const API = process.env.REACT_APP_API_URL || "";
const HOURS = Array.from({ length: 25 }, (_, i) => i);
const BIRTH_TYPES = [
  { value: "solar", label: "양력" },
  { value: "lunar", label: "음력" },
  { value: "lunar_leap", label: "음력 윤달" },
];
const CITY_GROUPS = [
  { label: "🇰🇷 국내", cities: ["서울","부산","대구","인천","광주","대전","울산","제주","수원","성남","용인","청주","전주","강릉","포항","창원","고양","춘천","목포","세종"] },
  { label: "🌏 동아시아", cities: ["도쿄","오사카","베이징","상하이","홍콩","타이베이","마카오","울란바토르"] },
  { label: "🌏 동남아시아", cities: ["방콕","싱가포르","쿠알라룸푸르","자카르타","마닐라","하노이","호치민","양곤","프놈펜","비엔티안"] },
  { label: "🌏 남아시아·중앙아시아", cities: ["뉴델리","뭄바이","다카","카라치","이슬라마바드","콜롬보","카트만두","타슈켄트","알마티","아스타나"] },
  { label: "🌍 중동", cities: ["두바이","아부다비","도하","리야드","테헤란","이스탄불","앙카라","바그다드","베이루트","암만","예루살렘","쿠웨이트시티"] },
  { label: "🌍 서유럽", cities: ["런던","파리","베를린","마드리드","로마","암스테르담","브뤼셀","취리히","비엔나","코펜하겐","스톡홀름","오슬로","헬싱키","리스본","더블린"] },
  { label: "🌍 동유럽·러시아", cities: ["모스크바","상트페테르부르크","키이우","바르샤바","프라하","부다페스트","부쿠레슈티","아테네","소피아","베오그라드","민스크","탈린","리가","빌뉴스"] },
  { label: "🌎 북미", cities: ["뉴욕","워싱턴DC","로스앤젤레스","시카고","샌프란시스코","시애틀","토론토","밴쿠버","오타와","멕시코시티"] },
  { label: "🌎 중남미·카리브", cities: ["보고타","상파울루","부에노스아이레스","리마","산티아고","카라카스","브라질리아","몬테비데오","아바나","파나마시티"] },
  { label: "🌏 오세아니아", cities: ["시드니","멜버른","캔버라","브리즈번","오클랜드","웰링턴"] },
  { label: "🌍 아프리카", cities: ["카이로","나이로비","요하네스버그","라고스","아디스아바바","카사블랑카","다카르","아크라","알제","튀니스"] },
];

const SERVICE_TYPES = [
  { key: "종합운세",  color: "#1971C2", icon: "✨", hook: "알고 나면 아무것도 아닌데, 모르면 평생 숙제입니다.", sub: "당신을 위한 5가지 정밀 운명 리포트" },
  { key: "신년운세",  color: "#7048E8", icon: "🌅", hook: "새해, 운의 흐름을 먼저 읽는 자가 앞서 갑니다.", sub: "올해 월별 운세 전체 흐름 분석" },
  { key: "연애운",    color: "#E64980", icon: "❤️", hook: "인연은 준비된 사람에게 찾아옵니다.", sub: "인연을 만나는 시기와 상대방 특징 분석" },
  { key: "재회운",    color: "#E03131", icon: "💔", hook: "그 사람 마음의 문턱, 지금은 몇 도일까요?", sub: "당신이 몰랐던 상대방의 속사정 분석" },
  { key: "결혼운",    color: "#C2255C", icon: "💍", hook: "우리는 정말 '타이밍'이 안 맞았던 걸까?", sub: "두 사람의 운이 일치하는 골든타임 탐지" },
  { key: "연애궁합",  color: "#FF6B9D", icon: "💑", hook: "끌리는 이유가 있습니다. 사주로 증명됩니다.", sub: "속궁합·성격궁합·이별수 전체 분석" },
  { key: "심층궁합",  color: "#0CA678", icon: "💕", hook: "결혼까지 가는 사람인지, 사주가 알고 있습니다.", sub: "결혼운·자녀운·이별위험시기 정밀 분석" },
  { key: "사업운",    color: "#2F9E44", icon: "📈", hook: "열심히 사는데 제자리라면, '운의 파도'를 체크하세요.", sub: "내 재물이 터지는 구체적인 시기 확인" },
  { key: "직업운",    color: "#F59F00", icon: "🎯", hook: "지금 이 선택, 운이 받쳐주는 타이밍인가요?", sub: "커리어 전환·승진의 최적 시기 분석" },
  { key: "건강운",    color: "#20C997", icon: "🌿", hook: "몸이 보내는 신호, 사주가 먼저 알고 있습니다.", sub: "취약 부위·사고수·수술수 시기 분석" },
];

/* 서비스별 결제 금액 */
const SVC_PRICE: Record<string, number> = {
  "종합운세": 9900, "신년운세": 9900,
  "연애운": 19900,  "재회운": 19900,
  "결혼운": 19900,  "연애궁합": 19900,
  "심층궁합": 29900,"사업운": 19900,
  "직업운": 19900,  "건강운": 19900,
};

/* 결제 후 잠금 해제 콘텐츠 설명 */
const SVC_UNLOCK: Record<string, string[]> = {
  "종합운세":  ["대운별 상세 운세", "연도별 세운 분석", "월별 운세 달력", "직업·재물·건강·사랑 점수"],
  "신년운세":  ["1~12월 월별 운세", "월별 용신·기신 날짜", "주의 시기 상세 설명"],
  "연애운":    ["인연 만날 가능성 높은 날짜", "상대방 외모·성격·직업 특징", "이별수·주의 시기"],
  "재회운":    ["재회 가능성 TOP 날짜", "감정 충돌 위험일", "상대방 마음 분석", "구체적 연락 전략"],
  "결혼운":    ["결혼 최적 골든타임 날짜", "상대방 책임감 상승 시기", "두 사람 합(合) 날짜"],
  "연애궁합":  ["속궁합 점수 및 분석", "성격 궁합 상세", "주의점 및 갈등 패턴", "이별수·위험 시기"],
  "심층궁합":  ["성격 궁합·속궁합", "주의점 상세", "결혼운·자녀운", "이별 위험 시기 전체"],
  "사업운":    ["계약·시작 최적일 TOP 3", "재물 터지는 시기", "업종 추천"],
  "직업운":    ["합격·승진 최적일 TOP 3", "이직 황금 타이밍", "직종 추천"],
  "건강운":    ["취약 부위 상세 분석", "사고수·수술수 위험 시기", "건강 관리 최적 시기"],
};

const ADULT_OPTIONS   = ["결혼운", "연애운", "사업(재물)운", "건강운"];
const MINOR_OPTIONS   = ["학업운", "가족/교우관계", "연애운", "건강운"];
const COMPAT_SERVICES = ["재회운", "결혼운", "심층궁합", "연애궁합"];

interface PartnerForm {
  name: string; gender: string; birth_type: string;
  birth_year: string; birth_month: string; birth_day: string;
  birth_hour: string; city: string;
}
const defaultPartner: PartnerForm = {
  name: "", gender: "female", birth_type: "solar",
  birth_year: "", birth_month: "", birth_day: "",
  birth_hour: "12", city: "서울",
};

export default function ClientPage() {
  const [step,        setStep]        = useState<0 | 1>(0);
  const [serviceType, setServiceType] = useState("종합운세");
  const [options,     setOptions]     = useState<string[]>([]);
  const [form,        setForm]        = useState({
    name: "", email: "", gender: "female",
    birth_year: "", birth_month: "", birth_day: "",
    birth_hour: "12", birth_minute: "0",
    birth_type: "solar", city: "서울", consent: false,
  });
  const [partner,  setPartner]  = useState<PartnerForm>(defaultPartner);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  // 제출 후 미리보기 상태
  const [preview, setPreview] = useState<{
    consultationId: number;
    text: string;
    serviceType: string;
    paymentEnabled: boolean;
    emailSent: boolean;
  } | null>(null);

  const age = useMemo(() => {
    const y = parseInt(form.birth_year);
    if (!y) return null;
    return new Date().getFullYear() - y;
  }, [form.birth_year]);
  const isMinor = age !== null && age < 20;
  const currentOptions = isMinor ? MINOR_OPTIONS : ADULT_OPTIONS;
  const isCompat = COMPAT_SERVICES.includes(serviceType);

  const set  = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }));
  const setP = (key: keyof PartnerForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setPartner(p => ({ ...p, [key]: e.target.value }));
  const toggleOption = (opt: string) =>
    setOptions(prev => prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]);

  const selectService = (svc: string) => {
    setServiceType(svc);
    setOptions([]);
    setStep(1);           // 서비스 선택 → 입력 폼으로 이동
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.consent) { setError("개인정보 활용에 동의해주세요."); return; }
    if (!form.name || !form.birth_year || !form.birth_month || !form.birth_day) {
      setError("필수 항목을 모두 입력해주세요."); return;
    }
    if (isCompat && (!partner.name || !partner.birth_year || !partner.birth_month || !partner.birth_day)) {
      setError("상대방 정보를 모두 입력해주세요."); return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/v1/client/submit`, {
        name: form.name,
        email: form.email || "",
        gender: form.gender,
        birth_year:  parseInt(form.birth_year),
        birth_month: parseInt(form.birth_month),
        birth_day:   parseInt(form.birth_day),
        birth_hour:  parseInt(form.birth_hour) || 12,
        birth_minute: parseInt(form.birth_minute) || 0,
        birth_type: form.birth_type,
        city: form.city,
        consent: true,
        service_type: serviceType,
        service_options: options,
        partner_info: isCompat ? {
          name: partner.name,
          gender: partner.gender,
          birth_type: partner.birth_type,
          birth_year:  parseInt(partner.birth_year),
          birth_month: parseInt(partner.birth_month),
          birth_day:   parseInt(partner.birth_day),
          birth_hour:  parseInt(partner.birth_hour) || 12,
          city: partner.city,
          compatibility_type: serviceType === "심층궁합" ? "deep"
                           : serviceType === "연애궁합" ? "casual"
                           : serviceType === "재회운"   ? "reunion"
                           : serviceType === "결혼운"   ? "marriage"
                           : "casual",
        } : null,
      });
      setPreview({
        consultationId: res.data.consultation_id,
        text: res.data.preview_text || "분석이 완료되었습니다.",
        serviceType: res.data.service_type || serviceType,
        paymentEnabled: res.data.payment_enabled ?? true,
        emailSent: res.data.email_sent ?? false,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || "제출 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 포트원 결제 핸들러
  const handlePayment = () => {
    if (!preview) return;
    const IMP = (window as any).IMP;
    if (!IMP) { alert("결제 모듈 로딩 중입니다. 잠시 후 다시 시도해주세요."); return; }
    IMP.init(process.env.REACT_APP_IMP_CODE || "imp00000000");
    IMP.request_pay({
      pg: "html5_inicis",
      pay_method: "card",
      merchant_uid: `saju_${preview.consultationId}_${Date.now()}`,
      name: `우연 사주 — ${preview.serviceType} 전체 리포트`,
      amount: SVC_PRICE[preview.serviceType] || 9900,
      buyer_name: form.name,
      buyer_email: form.email || "",
    }, async (rsp: any) => {
      if (rsp.success) {
        try {
          await axios.post(`${API}/api/v1/client/payment-verify`, {
            consultation_id: preview.consultationId,
            imp_uid: rsp.imp_uid,
            merchant_uid: rsp.merchant_uid,
          });
          window.location.href = `/report/${preview.consultationId}`;
        } catch {
          alert("결제는 완료되었으나 처리 중 오류가 발생했습니다. 관리자에게 문의해주세요.");
        }
      } else {
        alert(`결제 실패: ${rsp.error_msg || "다시 시도해주세요."}`);
      }
    });
  };

  /* ─── 미리보기 + 결제 화면 ─── */
  if (preview) {
    const svc = SERVICE_TYPES.find(s => s.key === preview.serviceType);
    const color = svc?.color || "#c09a6b";
    const unlockItems = SVC_UNLOCK[preview.serviceType] || [];
    const price = SVC_PRICE[preview.serviceType] || 9900;

    return (
      <>
        <Global />
        <Wrap>
          <Header>
            <Logo>🔮</Logo>
            <Title>우연 사주</Title>
          </Header>
          <SuccessCard>
            <ServiceBadge>{svc?.icon} {preview.serviceType}</ServiceBadge>
            <SuccessIcon>🔍</SuccessIcon>
            <SuccessTitle>분석 완료 — 미리보기</SuccessTitle>

            {/* 서술형 미리보기 */}
            <div style={{ position: "relative", margin: "16px 0 8px" }}>
              <div style={{
                padding: "16px 16px 0",
                background: `${color}0a`,
                border: `1.5px solid ${color}30`,
                borderRadius: 12,
                fontSize: "0.88rem",
                color: "#3d2b1f",
                lineHeight: 1.8,
                textAlign: "left",
                maxHeight: preview.paymentEnabled ? 180 : undefined,
                overflow: preview.paymentEnabled ? "hidden" : "visible",
              }}>
                {preview.text}
              </div>
              {/* 결제 필요 모드: 하단 페이드아웃 + 잠금 */}
              {preview.paymentEnabled && (
                <>
                  <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0, height: 80,
                    background: "linear-gradient(to bottom, transparent, #fff)",
                    borderRadius: "0 0 12px 12px", pointerEvents: "none",
                  }} />
                  <div style={{
                    position: "absolute", bottom: 10, left: 0, right: 0,
                    textAlign: "center", fontSize: "0.75rem", color: "#9c7b5a", fontWeight: 700,
                  }}>
                    🔒 전체 내용을 보려면 결제가 필요합니다
                  </div>
                </>
              )}
            </div>

            {preview.paymentEnabled ? (
              /* ── 정상 결제 모드 ── */
              <>
                <SubmitBtn
                  type="button"
                  $disabled={false}
                  onClick={handlePayment}
                  style={{ margin: "0 0 8px", width: "100%", background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
                >
                  ₩{price.toLocaleString()} 결제하고 전체 리포트 보기
                </SubmitBtn>
                {form.email && (
                  <SuccessDesc style={{ fontSize: "0.75rem", marginTop: 8 }}>
                    결제 완료 즉시 <strong>{form.email}</strong>으로도 리포트가 발송됩니다.
                  </SuccessDesc>
                )}
              </>
            ) : (
              /* ── 심사 중 무료 자동발송 모드 ── */
              <div style={{
                margin: "16px 0 8px",
                padding: "16px",
                background: "#ebfbee",
                border: "1.5px solid #8ce99a",
                borderRadius: 14,
                textAlign: "center",
              }}>
                <div style={{ fontSize: "1.4rem", marginBottom: 6 }}>✅</div>
                {preview.emailSent && form.email ? (
                  <div style={{ fontSize: "0.9rem", fontWeight: 800, color: "#2f9e44" }}>
                    <strong>{form.email}</strong>으로 리포트가 발송되었습니다!
                  </div>
                ) : (
                  <div style={{ fontSize: "0.9rem", fontWeight: 800, color: "#2f9e44" }}>
                    분석이 완료되었습니다.
                  </div>
                )}
                <SubmitBtn
                  type="button"
                  $disabled={false}
                  onClick={() => { window.location.href = `/report/${preview.consultationId}`; }}
                  style={{ margin: "12px 0 0", width: "100%", background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
                >
                  📖 리포트 바로 보기
                </SubmitBtn>
              </div>
            )}

            <ResetBtn
              style={{ marginTop: 12, background: "transparent", color: "#9c7b5a", border: "1.5px solid #e0d5c8", boxShadow: "none" }}
              onClick={() => {
                setPreview(null); setStep(0);
                setForm({ name: "", email: "", gender: "female", birth_year: "", birth_month: "", birth_day: "", birth_hour: "12", birth_minute: "0", birth_type: "solar", city: "서울", consent: false });
                setPartner(defaultPartner);
                setOptions([]); setError("");
              }}
            >
              다시 입력하기
            </ResetBtn>
          </SuccessCard>
        </Wrap>
      </>
    );
  }

  /* ─── STEP 0: 서비스 선택(훅) 페이지 ─── */
  if (step === 0) {
    return (
      <>
        <Global />
        <Wrap>
          <Header>
            <Logo>🔮</Logo>
            <Title>우연 사주</Title>
            <Sub>정밀 사주 분석 — 내담자 상담 접수</Sub>
          </Header>
          <HookWrap>
            <HookTitle>
              <HookTitleMain>어떤 운세를 알고 싶으신가요?</HookTitleMain>
              <HookTitleSub>원하는 운세를 선택하면 맞춤형 입력 화면으로 연결됩니다</HookTitleSub>
            </HookTitle>
            <ServiceCardGrid>
              {SERVICE_TYPES.map((s, idx) => (
                <ServiceCard
                  key={s.key}
                  type="button"
                  $color={s.color}
                  $idx={idx}
                  onClick={() => selectService(s.key)}
                >
                  <SvcIcon>{s.icon}</SvcIcon>
                  <SvcHook $color={s.color}>{s.hook}</SvcHook>
                  <SvcSub>{s.sub}</SvcSub>
                  <SvcBtn $color={s.color}>
                    {s.key} 보기 →
                  </SvcBtn>
                </ServiceCard>
              ))}
            </ServiceCardGrid>
          </HookWrap>
          {/* ── 테스트용 이메일 발송 버튼 ── */}
          <TestEmailBox />
        </Wrap>
      </>
    );
  }

  /* ─── STEP 1: 입력 폼 페이지 ─── */
  const svc = SERVICE_TYPES.find(s => s.key === serviceType)!;

  return (
    <>
      <Global />
      <Wrap>
        <Header>
          <Logo>🔮</Logo>
          <Title>우연 사주</Title>
        </Header>

        <FormWrap>
          <BackBtn type="button" onClick={() => { setStep(0); setError(""); }}>
            ← 운세 선택으로 돌아가기
          </BackBtn>

          <Card>
            {/* 선택된 서비스 헤더 */}
            <ServiceHeaderBand $color={svc.color}>
              <div>
                <ServiceHeaderIcon>{svc.icon}</ServiceHeaderIcon>
                <span style={{ fontSize: 13, fontWeight: 800, color: svc.color }}>{svc.key}</span>
              </div>
              <ServiceHeaderHook $color={svc.color}>{svc.hook}</ServiceHeaderHook>
              <ServiceHeaderSub>{svc.sub}</ServiceHeaderSub>
            </ServiceHeaderBand>

            {/* 관계운 필수 안내 */}
            {isCompat && (
              <CompatNoticeBand>
                <CompatNoticeTitle>⚠️ 상대방 정보 필수 입력</CompatNoticeTitle>
                <CompatNoticeText>
                  당신만 준비된다고 되는 것이 아닙니다.<br />
                  상대의 마음이 열리는 <strong>'문턱'</strong>을 계산하기 위해 상대방 정보가 반드시 필요합니다.
                </CompatNoticeText>
              </CompatNoticeBand>
            )}

            {/* 개별운세 서브옵션 */}
            <SubOptWrap $visible={serviceType === "개별운세"}>
              <SubOptInner>
                <SubOptLabel>
                  세부 항목 선택 (복수 선택 가능)
                  {isMinor && age !== null && (
                    <span style={{ marginLeft: 8, color: "#E03131" }}>
                      · 미성년({age}세) — 항목 자동 전환
                    </span>
                  )}
                </SubOptLabel>
                <ChipRow>
                  {currentOptions.map(opt => (
                    <Chip key={opt} type="button" $active={options.includes(opt)}
                      onClick={() => toggleOption(opt)}>
                      {opt}
                    </Chip>
                  ))}
                </ChipRow>
              </SubOptInner>
            </SubOptWrap>

            <FormBody onSubmit={handleSubmit}>
              <SectionTitle>기본 정보</SectionTitle>
              <FieldRow>
                <Field>
                  <Label>이름 *</Label>
                  <Input placeholder="홍길동" value={form.name} onChange={set("name")} inputMode="text" autoComplete="name" />
                </Field>
                <Field>
                  <Label>성별 *</Label>
                  <Select value={form.gender} onChange={set("gender")}>
                    <option value="female">여성</option>
                    <option value="male">남성</option>
                  </Select>
                </Field>
              </FieldRow>
              <Field style={{ marginBottom: 10 }}>
                <Label>이메일 (선택) — 결과 리포트를 이메일로 받을 수 있습니다</Label>
                <Input type="email" placeholder="example@email.com"
                  value={form.email} onChange={set("email")}
                  inputMode="email" autoComplete="email" />
              </Field>

              <SectionTitle>생년월일시</SectionTitle>
              <FieldRow>
                <Field>
                  <Label>구분</Label>
                  <Select value={form.birth_type} onChange={set("birth_type")}>
                    {BIRTH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Select>
                </Field>
                <Field>
                  <Label>연도 *</Label>
                  <Input type="number" placeholder="1990" min={1900} max={2020}
                    value={form.birth_year} onChange={set("birth_year")} />
                </Field>
              </FieldRow>
              <FieldRow>
                <Field>
                  <Label>월 *</Label>
                  <Input type="number" placeholder="1" min={1} max={12}
                    value={form.birth_month} onChange={set("birth_month")} />
                </Field>
                <Field>
                  <Label>일 *</Label>
                  <Input type="number" placeholder="1" min={1} max={31}
                    value={form.birth_day} onChange={set("birth_day")} />
                </Field>
                <Field>
                  <Label>시</Label>
                  <Select value={form.birth_hour} onChange={set("birth_hour")}>
                    <option value="">모름</option>
                    {HOURS.slice(0, 24).map(h => <option key={h} value={h}>{h}시</option>)}
                  </Select>
                </Field>
              </FieldRow>

              <SectionTitle>지역 (시간 보정용)</SectionTitle>
              <Field style={{ marginBottom: 0 }}>
                <Select value={form.city} onChange={set("city")}>
                  {CITY_GROUPS.map(g => (
                    <optgroup key={g.label} label={g.label}>
                      {g.cities.map(c => <option key={c} value={c}>{c}</option>)}
                    </optgroup>
                  ))}
                </Select>
              </Field>

              {/* 관계운 파트너 폼 */}
              <PartnerWrap $visible={isCompat}>
                <PartnerDivider>
                  <PartnerTitle>
                    {serviceType === "재회운" ? "💔" : serviceType === "결혼운" ? "💍" : "💕"} 상대방 정보
                  </PartnerTitle>
                  <PartnerSub>
                    {serviceType === "재회운"
                      ? "재회운: 상대방의 마음이 열리는 골든타임을 계산합니다"
                      : serviceType === "결혼운"
                      ? "결혼운: 두 사람의 운이 일치하는 최적 타이밍을 탐지합니다"
                      : serviceType === "연애궁합"
                      ? "연애궁합: 속궁합·성격궁합·이별수까지 분석합니다"
                      : "심층 궁합: 결혼운·자녀운·이별위험시기까지 분석합니다"}
                  </PartnerSub>
                  <FieldRow>
                    <Field>
                      <Label>상대방 이름 *</Label>
                      <Input placeholder="김영희" value={partner.name} onChange={setP("name")} />
                    </Field>
                    <Field>
                      <Label>성별 *</Label>
                      <Select value={partner.gender} onChange={setP("gender")}>
                        <option value="female">여성</option>
                        <option value="male">남성</option>
                      </Select>
                    </Field>
                  </FieldRow>
                  <FieldRow>
                    <Field>
                      <Label>구분</Label>
                      <Select value={partner.birth_type} onChange={setP("birth_type")}>
                        {BIRTH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </Select>
                    </Field>
                    <Field>
                      <Label>연도 *</Label>
                      <Input type="number" placeholder="1992" min={1900} max={2020}
                        value={partner.birth_year} onChange={setP("birth_year")} />
                    </Field>
                  </FieldRow>
                  <FieldRow>
                    <Field>
                      <Label>월 *</Label>
                      <Input type="number" placeholder="1" min={1} max={12}
                        value={partner.birth_month} onChange={setP("birth_month")} />
                    </Field>
                    <Field>
                      <Label>일 *</Label>
                      <Input type="number" placeholder="1" min={1} max={31}
                        value={partner.birth_day} onChange={setP("birth_day")} />
                    </Field>
                    <Field>
                      <Label>시</Label>
                      <Select value={partner.birth_hour} onChange={setP("birth_hour")}>
                        <option value="">모름</option>
                        {HOURS.slice(0, 24).map(h => <option key={h} value={h}>{h}시</option>)}
                      </Select>
                    </Field>
                  </FieldRow>
                  <Field style={{ marginBottom: 4 }}>
                    <Label>지역</Label>
                    <Select value={partner.city} onChange={setP("city")}>
                      {CITY_GROUPS.map(g => (
                        <optgroup key={g.label} label={g.label}>
                          {g.cities.map(c => <option key={c} value={c}>{c}</option>)}
                        </optgroup>
                      ))}
                    </Select>
                  </Field>
                </PartnerDivider>
              </PartnerWrap>

              <ConsentBox>
                <input type="checkbox" checked={form.consent}
                  onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))} />
                <span>
                  <a href="/legal/terms" target="_blank" rel="noreferrer" style={{ color: "#9c7b5a" }}>이용약관</a>
                  {" "}및{" "}
                  <a href="/legal/privacy" target="_blank" rel="noreferrer" style={{ color: "#9c7b5a" }}>개인정보 처리방침</a>
                  에 동의하며, 개인정보(이름, 생년월일)를 사주 분석 목적으로 수집·이용하는 것에 동의합니다. (필수)
                </span>
              </ConsentBox>
            </FormBody>

            {error && <ErrorMsg>{error}</ErrorMsg>}

            <SubmitBtn
              type="submit"
              $disabled={loading}
              onClick={handleSubmit as any}
              disabled={loading}
            >
              {loading ? "접수 중..." : `${svc.icon} ${svc.key} 접수하기`}
            </SubmitBtn>
          </Card>
        </FormWrap>

        {/* ── 사업자 정보 푸터 ── */}
        <SiteFooter />

        {/* ── 테스트용 이메일 발송 버튼 ── */}
        <TestEmailBox />
      </Wrap>
    </>
  );
}

/* ── 테스트 이메일 컴포넌트 ── */
function TestEmailBox() {
  const [open, setOpen]     = React.useState(false);
  const [email, setEmail]   = React.useState("wooyeonsaju@gmail.com");
  const [name, setName]     = React.useState("테스트");
  const [svc, setSvc]       = React.useState("결혼운");
  const [cid, setCid]       = React.useState("1");
  const [msg, setMsg]       = React.useState("");
  const [sending, setSending] = React.useState(false);
  const API = process.env.REACT_APP_API_URL || "";

  const send = async () => {
    setSending(true); setMsg("");
    try {
      await axios.post(`${API}/api/v1/client/test-email`, {
        to_email: email, name, service_type: svc,
        consultation_id: Number(cid) || 0,
      });
      setMsg("✅ 발송 성공! 받은 편지함을 확인하세요.");
    } catch (e: any) {
      setMsg(`❌ 실패: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ maxWidth: 520, margin: "16px auto 0", textAlign: "center" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none", border: "1.5px dashed #c09a6b",
          borderRadius: 10, padding: "8px 18px",
          fontSize: "0.75rem", color: "#9c7b5a",
          cursor: "pointer", fontFamily: "inherit",
        }}
      >
        🧪 테스트 이메일 발송
      </button>

      {open && (
        <div style={{
          marginTop: 12, padding: "18px 16px",
          background: "#fff", border: "1.5px solid #e0d5c8",
          borderRadius: 14, textAlign: "left",
          boxShadow: "0 2px 12px rgba(100,70,40,0.08)",
        }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#3d2b1f", marginBottom: 12 }}>
            테스트 이메일 — 결제 없이 바로 발송
          </div>
          {[
            { label: "받는 이메일", val: email, set: setEmail, ph: "이메일 주소" },
            { label: "이름",       val: name,  set: setName,  ph: "홍길동" },
            { label: "consultation_id", val: cid, set: setCid, ph: "1" },
          ].map(({ label, val, set, ph }) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: "0.7rem", color: "#9c7b5a", marginBottom: 2 }}>{label}</div>
              <input
                value={val} onChange={e => set(e.target.value)}
                placeholder={ph}
                style={{
                  width: "100%", padding: "8px 10px", borderRadius: 8,
                  border: "1.5px solid #e0d5c8", fontSize: "0.82rem",
                  fontFamily: "inherit", color: "#3d2b1f", outline: "none",
                }}
              />
            </div>
          ))}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: "0.7rem", color: "#9c7b5a", marginBottom: 2 }}>서비스 타입</div>
            <select
              value={svc} onChange={e => setSvc(e.target.value)}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 8,
                border: "1.5px solid #e0d5c8", fontSize: "0.82rem",
                fontFamily: "inherit", color: "#3d2b1f", background: "#fff",
              }}
            >
              {["종합운세","신년운세","재회운","결혼운","연애궁합","심층궁합","사업운","직업운"].map(s => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <button
            onClick={send} disabled={sending}
            style={{
              width: "100%", padding: "10px", borderRadius: 10,
              background: sending ? "#e0d5c8" : "linear-gradient(135deg,#c09a6b,#a07850)",
              color: "#fff", border: "none", fontSize: "0.85rem",
              fontWeight: 700, cursor: sending ? "default" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {sending ? "발송 중..." : "📨 테스트 메일 보내기"}
          </button>
          {msg && (
            <div style={{
              marginTop: 10, fontSize: "0.78rem", color: msg.startsWith("✅") ? "#2F9E44" : "#E03131",
              textAlign: "center", fontWeight: 700,
            }}>
              {msg}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
